import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Barbell, BowlFood, Ruler, CheckSquare, ChatCircleDots, Trophy, ChartLine, UsersFour,
  Medal, Sparkle,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const CARDS = [
  { to: "/coach/clients", label: "Clients",         icon: UsersFour,     desc: "Manage your general fitness clients" },
  { to: "/programs",      label: "Workout Plans",   icon: Barbell,       desc: "Assign plans to clients" },
  { to: "/nutrition",     label: "Nutrition",       icon: BowlFood,      desc: "Meals & macros logging" },
  { to: "/dashboard",     label: "Measurements",    icon: Ruler,         desc: "Weight, girths, progress photos" },
  { to: "/social",        label: "Challenges",      icon: Trophy,        desc: "Group challenges & leaderboards" },
  { to: "/messages",      label: "Messages",        icon: ChatCircleDots,desc: "Client conversations" },
  { to: "/analytics",     label: "Reports",         icon: ChartLine,     desc: "Client progress reports" },
  { to: "/coach/clients", label: "Attendance",      icon: CheckSquare,   desc: "Session check-ins" },
];

export default function FitnessTrainerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/trainer/dashboard").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  const kpi = (label, value, icon, testid) => (
    <div className="bg-surface-DEFAULT p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        {icon}
        <span className="label-tiny text-[9px] text-text-secondary">{label}</span>
      </div>
      <div className="font-heading text-2xl text-lime-300 tabular-nums">{value ?? "—"}</div>
    </div>
  );

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="fitness-trainer-dashboard">
      <div className="mb-8">
        <div className="label-tiny text-lime-300 mb-2">// FITNESS TRAINER MODE</div>
        <h1 className="font-heading text-4xl sm:text-5xl">TRAINER HUB</h1>
        <p className="text-text-secondary text-sm mt-2">
          {user?.name?.split(" ")[0] || "Trainer"} · General fitness coaching toolkit.
          Advanced S&amp;C modules are restricted (grant with permission override).
        </p>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mb-6" data-testid="ft-kpis">
          {kpi("CLIENTS",       data.roster_count,   <UsersFour size={14} weight="duotone" className="text-lime-300" />, "ft-kpi-clients")}
          {kpi("ACTIVE PLANS",  data.active_plans,   <Barbell size={14} weight="duotone" className="text-lime-300" />,    "ft-kpi-plans")}
          {kpi("WORKOUTS 7D",   data.workouts_7d,    <CheckSquare size={14} weight="duotone" className="text-lime-300" />,"ft-kpi-workouts")}
          {kpi("NEW PRS 7D",    data.prs_7d,         <Medal size={14} weight="duotone" className="text-yellow-400" />,    "ft-kpi-prs")}
        </div>
      )}

      {/* Live feeds */}
      {data && (data.recent_clients?.length > 0 || data.recent_workouts?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border border-slate-200 p-4" data-testid="ft-recent-clients">
            <div className="label-tiny mb-3">// RECENT CLIENTS ({data.recent_clients.length})</div>
            {data.recent_clients.length === 0 ? (
              <div className="text-xs text-text-secondary italic">No clients yet.</div>
            ) : (
              <div className="space-y-2">
                {data.recent_clients.map((c) => (
                  <Link key={c.client_id} to={`/coach/clients/${c.client_id}`} className="flex items-center gap-3 hover:bg-slate-100 px-2 py-1.5">
                    <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-[10px] font-bold">
                      {(c.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="text-sm font-heading">{c.name || "—"}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="border border-slate-200 p-4" data-testid="ft-recent-workouts">
            <div className="label-tiny mb-3">// RECENT WORKOUTS ({data.recent_workouts.length})</div>
            {data.recent_workouts.length === 0 ? (
              <div className="text-xs text-text-secondary italic">No workouts logged.</div>
            ) : (
              <div className="space-y-1.5 text-xs">
                {data.recent_workouts.map((w) => (
                  <div key={w.log_id} className="flex items-center justify-between gap-3">
                    <div className="truncate">
                      <span className="text-white">{w.client_name}</span>
                      <span className="text-text-secondary"> · {w.name}</span>
                    </div>
                    <span className="text-text-secondary tabular-nums">{new Date(w.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {data && data.roster_count === 0 && (
        <div className="border border-lime-400/30 bg-lime-400/[0.03] p-4 mb-6" data-testid="ft-empty-hint">
          <div className="label-tiny text-lime-300 mb-1"><Sparkle size={11} weight="fill" className="inline mr-1" />GET STARTED</div>
          <div className="text-xs text-text-secondary">
            You have no clients yet. Ask your academy admin to invite clients to your trainer roster (R5) — or import them from your existing list.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10" data-testid="ft-quickactions">
        {CARDS.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            data-testid={`ft-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="bg-surface-DEFAULT hover:bg-surface-elevated p-5 group transition"
          >
            <c.icon size={20} weight="duotone" className="text-lime-300 mb-3" />
            <div className="font-heading text-lg mb-1">{c.label.toUpperCase()}</div>
            <div className="text-xs text-text-secondary">{c.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
